import { useMemo } from 'react';

import { useActingProviderStore } from '@/store/actingProviderStore';
import { useAuthStore } from '@/store/authStore';
import { Membership, OrganizationModes } from '@/types/organization';

/**
 * O que cada tela pode mostrar. Uma função só decide, para a regra de
 * permissão (seção 5 do plano) não se espalhar em `if` por tela.
 *
 * - `team`: tela Equipe, membros, modos de operação.
 * - `actAs`: trocar o foco para outro profissional.
 * - `billing`: plano e assinatura da plataforma.
 * - `orgBranding`: logo, capa e cor da página pública da organização.
 * - `expenses`: gastos (da organização, ou os próprios no modo PROFESSIONAL).
 * - `ownPayment`: dados de pagamento próprios (Pix/link).
 * - `settlePayments`: dar baixa em cobrança (no caixa único, só dono/gerência).
 * - `teamPayments`: tela "Pagamentos da equipe" (caixa único, dono/gerência).
 * - `ownWhatsapp`: conectar o WhatsApp próprio.
 * - `allClients`: lista de clientes da organização inteira.
 * - `teamReports`: visão "Equipe" dos relatórios (só o dono).
 */
export type Capability =
  | 'team'
  | 'actAs'
  | 'billing'
  | 'orgBranding'
  | 'expenses'
  | 'ownPayment'
  | 'settlePayments'
  | 'teamPayments'
  | 'ownWhatsapp'
  | 'allClients'
  | 'teamReports';

export interface MembershipState {
  membership: Membership | null;
  /** Organização `SOLO` (ou API sem `membership`, que é o solo de hoje). */
  isSolo: boolean;
  /** Dono da organização — o solo também é dono da dele. */
  isOwner: boolean;
  /** Membro convidado por um dono. */
  isProfessional: boolean;
  /** Organização `TEAM` com mais alguém ativo além do dono. */
  hasTeam: boolean;
  /** Perdeu o acesso: o dono desativou o membro. */
  isDisabled: boolean;
  /** Organização sem plano de equipe confirmado: só a tela de planos abre. */
  teamPlanPending: boolean;
  modes: OrganizationModes | null;
  can: (capability: Capability) => boolean;
}

/**
 * Regra pura, fora do hook: o vínculo decide tudo, e a ausência dele é o
 * solo de hoje — assim a API antiga (sem `membership`) não esconde nada.
 */
export function resolveMembership(membership: Membership | null | undefined): MembershipState {
  const m = membership ?? null;
  const isTeam = m?.type === 'TEAM';
  const isSolo = !isTeam;
  const isProfessional = m?.role === 'PROFESSIONAL';
  const isOwner = !m || m.role === 'OWNER';
  const isDisabled = m?.status === 'DISABLED';
  const modes = isTeam ? (m?.modes ?? null) : null;
  const hasTeam = isTeam && (m?.activeMembers ?? 0) >= 2;
  const teamPlanPending = isTeam && isOwner && m?.teamPlanPending === true;

  const can = (capability: Capability): boolean => {
    if (isDisabled) return false;
    switch (capability) {
      case 'team':
      case 'actAs':
        return isTeam && isOwner;
      case 'billing':
      case 'orgBranding':
        return isOwner;
      case 'expenses':
      case 'ownPayment':
        return isOwner || modes?.paymentMode === 'PROFESSIONAL';
      case 'settlePayments':
        return isOwner || m?.role === 'MANAGER' || modes?.paymentMode !== 'ORGANIZATION';
      case 'teamPayments':
        return (
          hasTeam && (isOwner || m?.role === 'MANAGER') && modes?.paymentMode === 'ORGANIZATION'
        );
      case 'ownWhatsapp':
        return isOwner || modes?.messagingMode === 'PROFESSIONAL';
      case 'allClients':
        return isOwner || modes?.clientVisibility === 'SHARED';
      case 'teamReports':
        return hasTeam && isOwner;
    }
  };

  return {
    membership: m,
    isSolo,
    isOwner,
    isProfessional,
    hasTeam,
    isDisabled,
    teamPlanPending,
    modes,
    can,
  };
}

/** Papel e modo do usuário logado, lidos do `/user/me`. */
export function useMembership(): MembershipState {
  const membership = useAuthStore((s) => s.user?.membership);
  return useMemo(() => resolveMembership(membership), [membership]);
}

/**
 * Quem gerencia a subconta de recebimento automático (ativar, ligar/desligar,
 * formas aceitas, sessão dos componentes do Stripe) — espelha `canManage` de
 * `api/src/payment-account/payment-account-access.policy.ts` (achado da
 * revisão da T7.2): em caixa único só o dono ativa/gerencia, mesmo "agindo
 * como" outro profissional (a subconta é sempre a dele); fora disso
 * (por profissional/solo), só quem está vendo a própria tela.
 *
 * A capability `ownPayment` (acima) não basta aqui: ela olha só o papel
 * (`isOwner`), nunca o foco — um dono "agindo como" outro profissional no
 * modo "por profissional" continuaria vendo `true`, mas a API recusa
 * (`canManage` exige `ctx.isSelf` fora do caixa único). Único lugar que
 * decide isso; quem precisa saber se pode gerenciar a subconta chama este
 * hook, não `can('ownPayment')`.
 */
export function canManagePaymentAccount(
  membership: Membership | null | undefined,
  isActingAsOther: boolean,
): boolean {
  const m = membership ?? null;
  const isTeam = m?.type === 'TEAM';
  const isOwner = !m || m.role === 'OWNER';
  const isSingleTill = isTeam && m?.modes?.paymentMode === 'ORGANIZATION';
  if (isSingleTill) return isOwner;
  return !isActingAsOther;
}

export function useCanManagePaymentAccount(): boolean {
  const membership = useAuthStore((s) => s.user?.membership);
  const isActingAsOther = useActingProviderStore((s) => !!s.acting);
  return useMemo(
    () => canManagePaymentAccount(membership, isActingAsOther),
    [membership, isActingAsOther],
  );
}
